import React, { useState, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { makeStyles } from '@mui/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { addDays, format, isAfter, startOfToday } from 'date-fns';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { theme } from '../../theme/theme';
import {
  PadelBooking,
  Property,
  useGetPadelBookingsAvailableQuery,
  useGetPadelBookingsQuery,
  useGetPropertyByPropertyIdInvoicesQuery,
  usePostPadelBookingsMutation,
} from '../../redux/residentPortalApi';
import PadelCourtInvoicesSection from './PadelCourtInvoicesSection';
import PadelCourtStatement from './PadelCourtStatement';
import PadelCourtPaymentHistory from './PadelCourtPaymentHistory';

const PRICE_PER_SLOT_USD = 20;
const ALL_SLOTS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19];

const formatSlot = (hour: number) => {
  const start = hour < 12 ? `${hour}:00 AM` : hour === 12 ? '12:00 PM' : `${hour - 12}:00 PM`;
  const endHour = hour + 1;
  const end = endHour < 12 ? `${endHour}:00 AM` : endHour === 12 ? '12:00 PM' : `${endHour - 12}:00 PM`;
  return `${start} – ${end}`;
};

const useStyles = makeStyles({
  root: {
    padding: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 700,
    color: '#2c3e50',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  slotGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  slotCard: {
    borderRadius: '10px',
    padding: theme.spacing(2),
    cursor: 'pointer',
    border: '2px solid transparent',
    transition: 'all 0.18s ease',
    textAlign: 'center',
    userSelect: 'none',
  },
  slotAvailable: {
    backgroundColor: '#f0faf4',
    border: '2px solid #27ae60',
    '&:hover': {
      backgroundColor: '#d5f5e3',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(39,174,96,0.2)',
    },
  },
  slotSelected: {
    backgroundColor: '#1a5276',
    border: '2px solid #1a5276',
    color: '#fff',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(26,82,118,0.3)',
  },
  slotBooked: {
    backgroundColor: '#fdf2f2',
    border: '2px dashed #e74c3c',
    cursor: 'not-allowed',
    opacity: 0.65,
  },
  slotMyBooking: {
    backgroundColor: '#eaf6ff',
    border: '2px solid #2e86c1',
    cursor: 'default',
  },
  summaryCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    padding: theme.spacing(3),
    border: '1px solid #e8ecef',
  },
  bookingHistoryCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    border: '1px solid #e8ecef',
    overflow: 'hidden',
    marginTop: theme.spacing(4),
  },
  bookingRow: {
    padding: theme.spacing(2, 3),
    borderBottom: '1px solid #f0f0f0',
    '&:last-child': { borderBottom: 'none' },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  legendDot: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    flexShrink: 0,
  },
});

interface OwnProps {
  property: Property;
}

const PadelCourtTab: React.FC<OwnProps> = ({ property }) => {
  const classes = useStyles();

  const isPokugara = property?.development?.toLowerCase() === 'pokugara';
  const [expandedSection, setExpandedSection] = useState<string>('booking');

  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : '');
  };

  const tomorrow = addDays(startOfToday(), 1);
  const [selectedDate, setSelectedDate] = useState<Date | null>(tomorrow);
  const [selectedSlots, setSelectedSlots] = useState<number[]>([]);
  const [bookingError, setBookingError] = useState<string | null>(null);

  const dateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '';

  const { data: availability, isFetching: fetchingAvailability } = useGetPadelBookingsAvailableQuery(
    { date: dateStr },
    { skip: !dateStr }
  );

  const { data: myBookings, isFetching: fetchingMyBookings, refetch: refetchMyBookings } = useGetPadelBookingsQuery(
    { propertyId: property.id ?? '' },
    { skip: !property.id }
  );

  const { data: allPropertyInvoices } = useGetPropertyByPropertyIdInvoicesQuery(
    { propertyId: property?.id ?? '' },
    { skip: !property?.id || !isPokugara }
  );

  const padelCourtInvoices = useMemo(() => {
    return (allPropertyInvoices ?? []).filter(
      (inv) =>
        inv.invoice?.invoiceDescription === 'pedalCourtInvoiceBalance' ||
        (inv.invoice?.isGeneric && inv.invoice?.invoiceDescription === 'pedalCourtInvoiceBalance')
    );
  }, [allPropertyInvoices]);

  const outstandingPadelBalance = useMemo(() => {
    return (
      padelCourtInvoices
        ?.filter(
          (invoice) =>
            invoice.status?.toLowerCase() === 'unpaid' ||
            invoice.status?.toLowerCase() === 'partially paid'
        )
        ?.reduce((acc, invoice) => {
          return acc + (invoice.propertyInvoiceBalance ?? invoice.invoice?.totalCost ?? 0);
        }, 0) || 0
    );
  }, [padelCourtInvoices]);

  const [postBooking, { isLoading: isBooking }] = usePostPadelBookingsMutation();

  // Slots booked by this property on the selected date (confirmed)
  const myBookedSlotsOnDate = useMemo(() => {
    if (!myBookings || !dateStr) return new Set<number>();
    return new Set(
      myBookings
        .filter((b) => b.date === dateStr && b.status === 'confirmed')
        .flatMap((b) => b.slots)
    );
  }, [myBookings, dateStr]);

  const bookedByOthers = useMemo(() => {
    const available = new Set(availability?.availableSlots ?? ALL_SLOTS);
    return ALL_SLOTS.filter((s) => !available.has(s) && !myBookedSlotsOnDate.has(s));
  }, [availability, myBookedSlotsOnDate]);

  const totalAmount = selectedSlots.length * PRICE_PER_SLOT_USD;

  const toggleSlot = (slot: number) => {
    if (bookedByOthers.includes(slot) || myBookedSlotsOnDate.has(slot)) return;
    setSelectedSlots((prev) =>
      prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot]
    );
  };

  const getSlotState = (slot: number): 'available' | 'selected' | 'booked' | 'mine' => {
    if (myBookedSlotsOnDate.has(slot)) return 'mine';
    if (bookedByOthers.includes(slot)) return 'booked';
    if (selectedSlots.includes(slot)) return 'selected';
    return 'available';
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    setSelectedSlots([]);
    setBookingError(null);
  };

  const handleBookNow = async () => {
    if (selectedSlots.length === 0) return;
    setBookingError(null);
    try {
      const response = await postBooking({
        body: {
          propertyId: property.id ?? '',
          date: dateStr,
          slots: selectedSlots,
        },
      }).unwrap();
      setSelectedSlots([]);
      refetchMyBookings();
      if (response?.bookingId) {
        setBookingError(null);
      }
    } catch (err: any) {
      const msg =
        err?.data?.error?.message ??
        err?.data?.message ??
        'Failed to create booking. Please try again.';
      setBookingError(msg);
    }
  };

  const getStatusChip = (status: string) => {
    const map: Record<
      string,
      { label: string; color: 'success' | 'warning' | 'error' | 'default' }
    > = {
      confirmed: { label: 'Confirmed', color: 'success' },
      pending: { label: 'Pending', color: 'warning' },
      cancelled: { label: 'Cancelled', color: 'error' },
    };
    const s = map[status] ?? { label: status, color: 'default' };
    return <Chip label={s.label} color={s.color} size="small" />;
  };

  const isDateDisabled = (date: Date) => !isAfter(date, startOfToday());

  return (
    <Box className={classes.root}>
      {/* Padel Court Booking Accordion */}
      <Accordion
        disableGutters
        expanded={expandedSection === 'booking'}
        onChange={handleAccordionChange('booking')}
        sx={{
          mb: 2,
          borderRadius: 2,
          border: '1px solid #e5e7eb',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>Padel Court Booking</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2} mb={3}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #1a5276, #2e86c1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SportsTennisIcon sx={{ color: '#fff', fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} color="#2c3e50">
                Padel Court Booking
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reserve a court for your property and manage your upcoming bookings.
              </Typography>
            </Box>
          </Stack>

          <Grid container spacing={3}>
            {/* Left: Date picker + Slot grid */}
            <Grid item xs={12} md={8}>
              <Paper
                elevation={0}
                sx={{ borderRadius: '12px', border: '1px solid #e8ecef', p: 3 }}
              >
                {/* Date Picker */}
                <Typography className={classes.sectionTitle} variant="h6">
                  <EventAvailableIcon fontSize="small" />
                  Select Date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Booking Date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    shouldDisableDate={isDateDisabled}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        sx: { maxWidth: 280 },
                      },
                    }}
                  />
                </LocalizationProvider>

                <Divider sx={{ my: 3 }} />

                {/* Slot Grid */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1}
                >
                  <Typography className={classes.sectionTitle} variant="h6" sx={{ mb: 0 }}>
                    <AccessTimeIcon fontSize="small" />
                    Available Slots
                  </Typography>
                  {fetchingAvailability && <CircularProgress size={18} />}
                </Stack>

                {/* Legend */}
                <Stack direction="row" spacing={2} flexWrap="wrap" mb={2}>
                  {[
                    { color: '#e8f8ee', border: '#27ae60', label: 'Available' },
                    { color: '#1a5276', border: '#1a5276', label: 'Selected', textColor: '#fff' },
                    { color: '#fdf2f2', border: '#e74c3c', label: 'Taken' },
                    { color: '#eaf6ff', border: '#2e86c1', label: 'Yours' },
                  ].map((l) => (
                    <Stack direction="row" alignItems="center" spacing={0.8} key={l.label}>
                      <Box
                        className={classes.legendDot}
                        sx={{ backgroundColor: l.color, border: `2px solid ${l.border}` }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {l.label}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Box className={classes.slotGrid}>
                  {fetchingAvailability
                    ? ALL_SLOTS.map((s) => <Skeleton key={s} variant="rounded" height={72} />)
                    : ALL_SLOTS.map((slot) => {
                        const state = getSlotState(slot);
                        const stateClass =
                          state === 'selected'
                            ? classes.slotSelected
                            : state === 'booked'
                            ? classes.slotBooked
                            : state === 'mine'
                            ? classes.slotMyBooking
                            : classes.slotAvailable;

                        return (
                          <Tooltip
                            key={slot}
                            title={
                              state === 'booked'
                                ? 'Already booked by another property'
                                : state === 'mine'
                                ? 'Your confirmed booking'
                                : state === 'selected'
                                ? 'Click to deselect'
                                : 'Click to select'
                            }
                            arrow
                          >
                            <Box
                              className={`${classes.slotCard} ${stateClass}`}
                              onClick={() => toggleSlot(slot)}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{ color: state === 'selected' ? '#fff' : undefined }}
                              >
                                {formatSlot(slot)}
                              </Typography>
                              {state === 'booked' && (
                                <BlockIcon sx={{ fontSize: 16, color: '#e74c3c', mt: 0.5 }} />
                              )}
                              {state === 'mine' && (
                                <CheckCircleOutlineIcon
                                  sx={{ fontSize: 16, color: '#2e86c1', mt: 0.5 }}
                                />
                              )}
                              {state === 'selected' && (
                                <CheckCircleOutlineIcon
                                  sx={{ fontSize: 16, color: '#fff', mt: 0.5 }}
                                />
                              )}
                            </Box>
                          </Tooltip>
                        );
                      })}
                </Box>
              </Paper>
            </Grid>

            {/* Right: Summary + Action */}
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '12px',
                  border: '1px solid #e8ecef',
                  p: 3,
                  position: 'sticky',
                  top: 20,
                }}
              >
                <Typography variant="h6" fontWeight={700} color="#2c3e50" mb={2}>
                  Booking Summary
                </Typography>

                {selectedSlots.length === 0 ? (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 4,
                      color: '#95a5a6',
                      border: '2px dashed #e0e0e0',
                      borderRadius: '10px',
                    }}
                  >
                    <AccessTimeIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
                    <Typography variant="body2">
                      Select time slots on the left to begin
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Stack spacing={1} mb={2}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Date
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {dateStr}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Slots
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {selectedSlots.length} hour{selectedSlots.length > 1 ? 's' : ''}
                        </Typography>
                      </Stack>
                      <Box>
                        <Typography variant="body2" color="text.secondary" mb={0.5}>
                          Selected times
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {[...selectedSlots]
                            .sort((a, b) => a - b)
                            .map((s) => (
                              <Chip
                                key={s}
                                label={formatSlot(s)}
                                size="small"
                                onDelete={() => toggleSlot(s)}
                                sx={{ fontSize: '0.72rem' }}
                              />
                            ))}
                        </Stack>
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Box
                      sx={{
                        backgroundColor: '#eaf3fa',
                        border: '1px solid #aed6f1',
                        borderRadius: '10px',
                        p: 2,
                        mb: 2,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {selectedSlots.length} slot{selectedSlots.length !== 1 ? 's' : ''} × $20 /
                        slot
                      </Typography>
                      <Typography variant="h4" fontWeight={800} color="#1a5276" mt={0.5}>
                        ${totalAmount} USD
                      </Typography>
                    </Box>

                    {bookingError && (
                      <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                        {bookingError}
                      </Alert>
                    )}

                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      disabled={isBooking || selectedSlots.length === 0}
                      onClick={handleBookNow}
                      sx={{
                        background: 'linear-gradient(135deg, #1a5276, #2e86c1)',
                        borderRadius: '10px',
                        fontWeight: 700,
                        textTransform: 'none',
                        fontSize: '1rem',
                        py: 1.5,
                      }}
                    >
                      {isBooking ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CircularProgress size={18} color="inherit" />
                          <span>Booking…</span>
                        </Stack>
                      ) : (
                        'Book'
                      )}
                    </Button>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Booking History */}
          <Box mt={5}>
            <Typography className={classes.sectionTitle} variant="h6">
              <EventAvailableIcon fontSize="small" />
              My Bookings
            </Typography>

            <Paper elevation={0} className={classes.bookingHistoryCard}>
              {fetchingMyBookings ? (
                <Box p={3}>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} height={60} sx={{ mb: 1 }} />
                  ))}
                </Box>
              ) : !myBookings?.length ? (
                <Box sx={{ textAlign: 'center', py: 5, color: '#aab0b8' }}>
                  <SportsTennisIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                  <Typography variant="body2">No bookings yet for this property.</Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      px: 3,
                      py: 1.5,
                      backgroundColor: '#f8f9fa',
                      borderBottom: '1px solid #e8ecef',
                      display: 'grid',
                      gridTemplateColumns: '130px 1fr auto',
                      gap: 2,
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      DATE
                    </Typography>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      SLOTS
                    </Typography>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      STATUS
                    </Typography>
                  </Box>
                  {[...myBookings]
                    .sort((a, b) => b.date.localeCompare(a.date))
                    .map((booking: PadelBooking) => (
                      <Box
                        key={booking.id}
                        className={classes.bookingRow}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '130px 1fr auto',
                          gap: 2,
                          alignItems: 'center',
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {booking.date}
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {[...booking.slots]
                            .sort((a, b) => a - b)
                            .map((s) => (
                              <Chip
                                key={s}
                                label={formatSlot(s)}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            ))}
                        </Stack>
                        {getStatusChip(booking.status)}
                      </Box>
                    ))}
                </>
              )}
            </Paper>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Pokugara-only Accordions for Padel Court Invoices, Statement, and Payment History */}
      {isPokugara && (
        <>
          {/* Padel Court Invoices Accordion */}
          <Accordion
            disableGutters
            expanded={expandedSection === 'invoices'}
            onChange={handleAccordionChange('invoices')}
            sx={{
              mb: 2,
              borderRadius: 2,
              border: '1px solid #e5e7eb',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>Padel Court Invoices</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <PadelCourtInvoicesSection
                property={property}
                invoices={padelCourtInvoices}
                outstandingPadelBalance={outstandingPadelBalance}
              />
            </AccordionDetails>
          </Accordion>

          {/* Padel Court Statement Accordion */}
          <Accordion
            disableGutters
            expanded={expandedSection === 'statement'}
            onChange={handleAccordionChange('statement')}
            sx={{
              mb: 2,
              borderRadius: 2,
              border: '1px solid #e5e7eb',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>Padel Court Statement</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <PadelCourtStatement
                propertyId={property?.id ?? ''}
                invoices={padelCourtInvoices}
              />
            </AccordionDetails>
          </Accordion>

          {/* Payments History Accordion */}
          <Accordion
            disableGutters
            expanded={expandedSection === 'payments'}
            onChange={handleAccordionChange('payments')}
            sx={{
              mb: 2,
              borderRadius: 2,
              border: '1px solid #e5e7eb',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>Payments History</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <PadelCourtPaymentHistory propertyId={property?.id ?? ''} />
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Box>
  );
};

export default PadelCourtTab;
